<script setup>
/**
 * ImmersivePromoPane
 * Full-screen feed pane for a MULTI-ITEM madregot promotion (the "מבצעים"
 * department's consolidated card, immersive edition). Shows a crossfading
 * slideshow of the promotion's items, the red/gold madregot step rail with the
 * live cumulative group fill, price range, live in-cart status — and one big
 * CTA that opens the promo items modal where quantities are chosen per item.
 *
 * Emits:
 *   open-items(promo)        — CTA / rail tap → host opens ImmersivePromoItemsModal
 *   celebrate({kind,label})  — a madregot step was newly reached (current pane only)
 */
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useCatalogStore } from '../../../../app/store/catalog.js'
import { useCartStore } from '../../../../app/store/cart.js'
import {
  getPromoMakats,
  getPromoSteps,
  computeCumulativeMadregotApplication,
} from '../../../../core/utils/promoCalc.js'
import { VAT_MULTIPLIER } from '../../../../core/config/pricingConstants.js'
import { prefersReducedMotion } from '../../../../core/composables/useImmersiveSwipe.js'
import ImmersiveDiscountBar from './ImmersiveDiscountBar.vue'

const props = defineProps({
  promo: { type: Object, required: true },
  showPrices: { type: Boolean, default: true },
  isCurrent: { type: Boolean, default: false },
})

const emit = defineEmits(['open-items', 'celebrate'])

const catalogStore = useCatalogStore()
const cartStore = useCartStore()

const isFirstOrder = computed(() => props.promo.first_order === true)
const isWeekly = computed(() => !isFirstOrder.value && props.promo.weeklyBenefit === true)

// ── Items in the promotion (available in this client's catalog) ─
const promoItems = computed(() =>
  getPromoMakats(props.promo)
    .map((makat) => catalogStore.itemsByMakat.get(makat))
    .filter(Boolean)
    .filter((item) => item.status !== 'אסור למכירה')
)

const itemCount = computed(() => promoItems.value.length)

// ── Cumulative madregot application (live with the cart) ────────
const cartLinesByMakat = computed(() => {
  const result = {}
  for (const line of cartStore.linesArray) {
    result[line.makat] = { qty: line.qty, addedAt: line.addedAt }
  }
  return result
})

const application = computed(() =>
  computeCumulativeMadregotApplication({
    promo: props.promo,
    cartLinesByMakat: cartLinesByMakat.value,
  })
)

const totalQty = computed(() => application.value.totalQty)

// Group money total (promo units at the applied step price, over-max units at
// each item's regular price), incl. VAT — consistent with the pane line total.
const totalWithVat = computed(() => {
  const app = application.value
  if (!app.totalQty) return null
  let sum = 0
  for (const [makat, alloc] of Object.entries(app.allocationsByMakat)) {
    const item = catalogStore.itemsByMakat.get(makat)
    const base = parseFloat(item?.price) || 0
    const unit = app.appliedStep ? app.appliedStep.unitPrice : base
    sum += alloc.promoQty * unit + alloc.regularQty * base
  }
  return (sum * VAT_MULTIPLIER).toFixed(2)
})

// ── Step rail model (red / gold madregot ladder) ────────────────
const basePrice = computed(() => {
  const first = promoItems.value.find((it) => parseFloat(it.price) > 0)
  return first ? parseFloat(first.price).toFixed(2) : null
})

const steps = computed(() => getPromoSteps(props.promo))

const railLadder = computed(() => {
  if (!props.showPrices || steps.value.length === 0) return null
  const appliedKey = application.value.appliedStep?.stepKey || null
  const ladder = steps.value.map((s) => ({
    minQty: s.amount,
    unitPriceAfterDiscount: s.price.toFixed(2),
    isActive: s.stepKey === appliedKey,
  }))
  if (basePrice.value) {
    ladder.unshift({
      minQty: 0,
      unitPriceAfterDiscount: basePrice.value,
      isBase: true,
      isActive: false,
    })
  }
  return ladder
})

const hasRail = computed(() => !!railLadder.value && railLadder.value.length > 1)

// Promo step price range, e.g. "₪6.50 - ₪7.00" (single value when equal).
const stepPriceRange = computed(() => {
  if (steps.value.length === 0) return null
  const prices = steps.value.map((s) => s.price)
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return `₪${min.toFixed(2)}`
  return `₪${min.toFixed(2)} - ₪${max.toFixed(2)}`
})

// ── Days remaining chip (urgency, mirrors the catalog promo card) ─
const daysLeftText = computed(() => {
  const endDate = props.promo.date_end_date || props.promo.end_date
  if (!endDate) return null
  const end = endDate.toDate ? endDate.toDate() : new Date(endDate)
  if (isNaN(end.getTime())) return null
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
  if (days <= 1) return 'יום אחרון!'
  if (days < 10) return `נותרו ${days} ימים`
  return null
})

// ── Image slideshow (promo image + item images, crossfade) ──────
const slideshowImages = computed(() => {
  const images = []
  if (props.promo.image) images.push(props.promo.image)
  for (const item of promoItems.value) {
    if (item.image && !images.includes(item.image)) images.push(item.image)
  }
  return images
})

const activeImageIndex = ref(0)
let slideshowTimer = null

function stopSlideshow() {
  if (slideshowTimer) clearInterval(slideshowTimer)
  slideshowTimer = null
}

function startSlideshow() {
  stopSlideshow()
  if (!props.isCurrent || slideshowImages.value.length <= 1 || prefersReducedMotion()) return
  slideshowTimer = setInterval(() => {
    activeImageIndex.value = (activeImageIndex.value + 1) % slideshowImages.value.length
  }, 4000)
}

// Only the current pane animates its slideshow (neighbors stay still).
watch(() => props.isCurrent, startSlideshow)
onMounted(startSlideshow)
onUnmounted(stopSlideshow)

// ── Celebration: a madregot step was newly reached / deepened ───
const appliedStepPrice = computed(() => {
  const p = application.value.appliedStep?.unitPrice
  return typeof p === 'number' && p > 0 ? p : null
})

watch(appliedStepPrice, (nv, ov) => {
  if (!props.isCurrent) return
  if (nv != null && (ov == null || nv < ov) && totalQty.value > 0) {
    emit('celebrate', { kind: isFirstOrder.value ? 'promo' : 'promo', label: `₪${nv.toFixed(2)}` })
  }
})

function openItems() {
  emit('open-items', props.promo)
}
</script>

<template>
  <div
    class="imm-promo-pane"
    :class="{ 'has-rail': hasRail, 'no-rail': !hasRail, 'is-gold': isFirstOrder }"
  >
    <!-- Items-count sticker — pinned to the pane's visible top corner -->
    <span class="imm-pp-count-sticker">{{ itemCount }} פריטים</span>

    <!-- Slideshow hero -->
    <div class="imm-pp-image-zone">
      <template v-if="slideshowImages.length > 0">
        <img
          v-for="(img, idx) in slideshowImages"
          :key="img"
          :src="img"
          :alt="promo.label || 'מבצע'"
          class="imm-pp-image"
          :class="{ 'is-active': idx === activeImageIndex % slideshowImages.length }"
        />
      </template>
      <div v-else class="imm-pp-image-placeholder">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path
            d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"
          />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      </div>
    </div>

    <!-- Content panel -->
    <div class="imm-pp-panel">
      <div class="imm-pp-badges">
        <span
          class="imm-pp-badge"
          :class="isFirstOrder ? 'is-gold' : isWeekly ? 'is-blue' : 'is-red'"
        >
          {{ isFirstOrder ? 'מבצע לקוחות חדשים' : isWeekly ? 'הטבה שבועית' : 'מבצע' }}
        </span>
        <span v-if="daysLeftText" class="imm-pp-days">{{ daysLeftText }}</span>
      </div>

      <h2 class="imm-pp-name">{{ promo.label || 'מבצע' }}</h2>
      <p v-if="promo.description" class="imm-pp-desc">{{ promo.description }}</p>

      <div v-if="showPrices" class="imm-pp-prices">
        <span v-if="basePrice" class="imm-pp-base">מחיר רגיל ₪{{ basePrice }}</span>
        <span v-if="stepPriceRange" class="imm-pp-range">במבצע {{ stepPriceRange }}</span>
      </div>
      <div v-else class="imm-pp-guest">התחבר לצפייה במחירים</div>

      <!-- Live group status (reserved line — no layout shift on first add) -->
      <div class="imm-pp-status" :class="{ 'is-reserved': !totalQty }">
        <template v-if="totalQty">
          בעגלה {{ totalQty }} יח'
          <template v-if="totalWithVat"> · ₪{{ totalWithVat }} כולל מע"מ</template>
        </template>
      </div>

      <!-- THE control: opens the per-item quantities modal. The next-step hint
           is NOT an in-layout element (it grew the panel after adds); the host
           overlay surfaces it as a transient toast when the items modal closes. -->
      <button type="button" class="imm-pp-cta" :class="{ 'is-idle': !totalQty }" @click="openItems">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>בחירת מוצרי המבצע</span>
        <span v-if="totalQty" class="imm-pp-cta-badge">{{ totalQty }}</span>
      </button>
    </div>

    <!-- Madregot step rail — tapping a step opens the items modal (quantities
         are chosen per item there). -->
    <ImmersiveDiscountBar
      v-if="hasRail"
      class="imm-disc"
      variant="promo"
      :gold="isFirstOrder"
      :caption="isFirstOrder ? 'מבצע חדשים' : 'מבצע'"
      :ladder="railLadder"
      :total-qty="totalQty"
      @select-tier="openItems"
    />
  </div>
</template>

<style scoped>
.imm-promo-pane {
  position: relative;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  /* Panel row is CONTENT-sized (all its children are clamped/fixed-height), the
     elastic hero image absorbs the slack — so the panel can never overflow in
     portrait. On extreme viewports (landscape phones) the panel row compresses
     (min 0) and its overflow-y:auto safety net takes over; the swipe engine's
     shouldIgnore hook recognizes .imm-pp-panel, so it owns that scroll cleanly. */
  grid-template-rows: minmax(96px, 1fr) minmax(0, max-content);
  grid-template-areas:
    'image'
    'panel';
}

.imm-promo-pane.has-rail {
  padding-inline: var(--imm-disc-gutter, 72px);
}

.imm-promo-pane > * {
  min-width: 0;
  min-height: 0;
}

/* Step rail — absolute overlay centred in the physical-left gutter
   (mobile/tablet); PC promotes it to a real grid column below. */
.imm-promo-pane :deep(.imm-disc) {
  position: absolute;
  inset-block: 6px;
  inset-inline-end: calc((var(--imm-disc-gutter, 72px) - var(--imm-disc-w, 64px)) / 2);
  z-index: 4;
}

/* ── Slideshow hero ── */
.imm-pp-image-zone {
  grid-area: image;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8px 12px;
}

.imm-pp-count-sticker {
  position: absolute;
  top: 8px;
  inset-inline-start: 12px;
  z-index: 2;
  padding: 4px 10px;
  border-radius: 10px;
  border: 1px solid var(--imm-pill-border, rgba(255, 255, 255, 0.14));
  background: var(--imm-pill-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.85));
  font-size: var(--imm-ident-size, 0.78rem);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.imm-pp-image {
  position: absolute;
  inset: 8px 12px;
  width: calc(100% - 24px);
  height: calc(100% - 16px);
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.7s ease;
  filter: drop-shadow(0 18px 28px rgba(0, 0, 0, 0.28));
}

.imm-pp-image.is-active {
  opacity: 1;
}

.imm-pp-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imm-pp-image-placeholder svg {
  width: 88px;
  height: 88px;
  color: var(--imm-placeholder, rgba(255, 255, 255, 0.22));
}

/* ── Content panel ── */
.imm-pp-panel {
  grid-area: panel;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 18px 16px;
  overflow-y: auto;
  text-align: center;
}

.imm-pp-badges {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.imm-pp-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #ffffff;
  white-space: nowrap;
}

.imm-pp-badge.is-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.imm-pp-badge.is-gold {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.imm-pp-badge.is-blue {
  background: var(--color-info);
}

.imm-pp-days {
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--imm-warning, #fbbf24);
}

.imm-pp-name {
  margin: 0;
  font-size: var(--imm-name-size, 1.15rem);
  font-weight: 800;
  line-height: 1.25;
  color: var(--imm-text, #ffffff);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.imm-pp-desc {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.65));
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 460px;
}

.imm-pp-prices {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.imm-pp-base {
  font-size: var(--imm-orig-size, 0.95rem);
  font-weight: 600;
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.5));
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
}

.imm-pp-range {
  font-size: calc(var(--imm-price-size, 2.3rem) * 0.62);
  font-weight: 800;
  color: #ef4444;
  font-variant-numeric: tabular-nums;
}

.imm-promo-pane.is-gold .imm-pp-range {
  color: #f59e0b;
}

.imm-pp-guest {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.65));
}

.imm-pp-status {
  font-size: var(--imm-total-size, 0.9rem);
  font-weight: 700;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.82));
  text-shadow: var(--imm-total-glow, 0 0 14px rgba(96, 165, 250, 0.4));
  line-height: 1.3;
  min-height: 1.3em;
  white-space: nowrap;
}

.imm-pp-status.is-reserved {
  visibility: hidden;
}

/* ── CTA — the star control ── */
.imm-pp-cta {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: var(--imm-step-size, 64px);
  padding: 0 28px;
  width: 100%;
  max-width: 420px;
  border: none;
  border-radius: 18px;
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
  color: #ffffff;
  font-size: calc(var(--imm-step-font, 1.7rem) * 0.62);
  font-weight: 800;
  font-family: inherit;
  letter-spacing: 0.3px;
  cursor: pointer;
  box-shadow: 0 10px 26px rgba(220, 38, 38, 0.35);
  transition:
    transform 0.12s ease,
    box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-promo-pane.is-gold .imm-pp-cta {
  background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%);
  box-shadow: 0 10px 26px rgba(217, 119, 6, 0.35);
}

.imm-pp-cta:active {
  transform: scale(0.96);
}

/* Gentle invite pulse until the user engages with the promo. */
.imm-pp-cta.is-idle {
  animation: imm-pp-cta-pulse 2.4s ease-in-out infinite;
}

@keyframes imm-pp-cta-pulse {
  0%,
  100% {
    box-shadow: 0 10px 26px rgba(220, 38, 38, 0.35);
  }
  50% {
    box-shadow: 0 10px 34px rgba(220, 38, 38, 0.6);
  }
}

.imm-pp-cta svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.imm-pp-cta-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 1rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

@media (hover: hover) {
  .imm-pp-cta:hover {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 14px 34px rgba(220, 38, 38, 0.5);
  }
}

/* ── Tablet (768–1279) ── */
@media (min-width: 768px) and (max-width: 1279px) {
  .imm-pp-panel {
    justify-content: safe center;
    gap: 14px;
  }

  .imm-pp-desc {
    font-size: 0.95rem;
  }
}

/* ── PC (≥1280): editorial split — slideshow right, content centre, rail left ── */
@media (min-width: 1280px) {
  .imm-promo-pane {
    grid-template-columns: minmax(0, 1.35fr) minmax(400px, 0.9fr) auto;
    grid-template-rows: 1fr;
    grid-template-areas: 'image panel ladder';
    column-gap: clamp(16px, 2vw, 40px);
  }

  .imm-promo-pane.has-rail {
    padding-inline: 0;
  }

  .imm-promo-pane.no-rail {
    grid-template-columns: minmax(0, 1.4fr) minmax(440px, 0.85fr);
    grid-template-areas: 'image panel';
  }

  .imm-promo-pane :deep(.imm-disc) {
    position: static;
    inset: auto;
    grid-area: ladder;
    align-self: stretch;
  }

  .imm-pp-panel {
    justify-content: safe center;
    align-items: flex-start;
    text-align: start;
    gap: 16px;
    padding-inline: clamp(20px, 3vw, 48px);
  }

  .imm-pp-badges,
  .imm-pp-prices {
    justify-content: flex-start;
  }

  .imm-pp-desc {
    font-size: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .imm-pp-image {
    transition: none;
  }

  .imm-pp-cta.is-idle {
    animation: none;
  }

  .imm-pp-cta {
    transition: none;
  }
}
</style>
